import { RemoveSingleMemberFromEntity } from "@components/remove-single-member-from-entity";
import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import {
  isGroupMember,
  isProjectMemberBase,
} from "@custom-types/member-type-guards";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { getCompanyByIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { MenuItem, DeleteIcon } from "@flat-ui";
import { useState } from "react";

interface Props {
  /** The member to potentially remove */
  member: SdbMemberTypes;

  /** Company ID */
  companyId: string;

  /** Context in which the members table is used */
  subjectType: MemberTableSubject;

  /** The selected project (for project member tables) */
  selectedProject?: SdbProject | null;

  /** The selected group (for group member tables) */
  group?: FaroCoreApiTypes.GroupDetailResponse | null;

  /** Callback to close the parent 3-dot menu */
  onMenuClose: () => void;
}

/**
 * Menu item that opens a remove-member confirmation dialog.
 * Handles project, group, and workspace member removal.
 * For team members, use TeamRemoveMenuButton instead.
 */
export function MemberRemoveMenuButton({
  member,
  companyId,
  subjectType,
  selectedProject,
  group,
  onMenuClose,
}: Props): JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const selectedSdbCompany = useAppSelector(getCompanyByIdSelector(companyId));

  function closeDialog(): void {
    setIsDialogOpen(false);
    onMenuClose();
  }

  return (
    <>
      <MenuItem
        label="Remove member"
        Icon={DeleteIcon}
        onClick={() => setIsDialogOpen(true)}
      />
      {isDialogOpen && renderRemoveDialog()}
    </>
  );

  /** Renders the remove member dialog based on the current subject type */
  function renderRemoveDialog(): JSX.Element | null {
    if (
      selectedProject &&
      subjectType === "project" &&
      isProjectMemberBase(member)
    ) {
      return (
        <RemoveSingleMemberFromEntity
          companyId={companyId}
          member={member}
          entity={selectedProject}
          entityName="project"
          isOpenOnMount
          onClose={closeDialog}
        />
      );
    }

    if (group && subjectType === "group" && isGroupMember(member)) {
      return (
        <RemoveSingleMemberFromEntity
          companyId={companyId}
          member={member}
          entity={group}
          entityName="group"
          isOpenOnMount
          onClose={closeDialog}
        />
      );
    }

    if (subjectType === "workspace" && selectedSdbCompany) {
      return (
        <RemoveSingleMemberFromEntity
          companyId={companyId}
          member={member}
          entity={selectedSdbCompany}
          entityName="workspace"
          isOpenOnMount
          onClose={closeDialog}
        />
      );
    }

    return null;
  }
}
